import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';

import { ORG_SEARCH_BY_NAME_OR_DOMAIN } from 'src/graphql/queries';

export const OrganizationContext = createContext();

export function OrganizationProvider({ children }) {
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [searchedOrgs, setSearchedOrgs] = useState([]);

  const [searchOrgs, { loading, error, data }] = useLazyQuery(ORG_SEARCH_BY_NAME_OR_DOMAIN);

  const debouncedSearch = useCallback((searchTerm) => {
    const debouncedFn = debounce((term) => {
      if (term) {
        searchOrgs({ variables: { search: `%${term}%` } });
      } else {
        setSearchedOrgs([]); // Clear results if search term is empty
      }
    }, 500);
    
    debouncedFn(searchTerm);
  }, [searchOrgs]);

  useEffect(() => {
    if (data && data.organisations) {
      setSearchedOrgs(data.organisations);
    }
  }, [data]);

  // Load initial organization from localStorage
  useEffect(() => {
    try {
      const storedOrg = localStorage.getItem('currentOrganization');
      if (storedOrg) {
        setCurrentOrganization(JSON.parse(storedOrg));
      }
    } catch (e) {
      console.error("Failed to parse organization from localStorage", e);
      localStorage.removeItem('currentOrganization');
    }
  }, []);
  
  // Persist current organization to localStorage
  useEffect(() => {
    try {
      if (currentOrganization) {
        localStorage.setItem('currentOrganization', JSON.stringify(currentOrganization));
      } else {
        localStorage.removeItem('currentOrganization');
      }
    } catch (e) {
      console.error("Failed to save organization to localStorage", e);
    }
  }, [currentOrganization]);

  const value = useMemo(() => ({
    organizations: searchedOrgs,
    currentOrganization,
    setCurrentOrganization,
    searchOrganizations: debouncedSearch,
    loading,
    error,
  }), [searchedOrgs, currentOrganization, debouncedSearch, loading, error]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

OrganizationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}; 